<template>
  <BaseCard2
    :id="profile?.id"
    :title="'Profile'"
    :username="profileUsername"
    :uidType="'task'"
    :state="profileState"
    :description="profileDescription"
    :descriptionClickable="true"
    :data="profileData"
    :footerDate="profileCreatedAt"
    :showDelete="true"
    :width="500"
    class="profile-card"
    @card-click="handleProfileCardClick"
    @description-changed="saveDescription"
    @delete-click="deleteProfile"
  >
    <template #header-right-before-delete>
      <IconButton
        v-if="profile?.id"
        v-tooltip="`View charts for profile`"
        class="header-icon"
        :icon="'analytics'"
        :width="24"
        :height="24"
        :type="''"
        @click.stop="openProfileCharts"
      />
    </template>

    <template #body>
      <div class="body-actions">
        <span class="body-actions__title">Validation Sessions</span>
        <IconButton
          class="add-validation-icon"
          :icon="'add'"
          :width="20"
          :height="20"
          @click.stop="openCreateValidationSessionDialog"
        />
      </div>

      <div class="validation-mini-list-wrap">
        <ValiListMini
          :sessions="profileValSessions"
          :emptyText="'No validation sessions attached.'"
        />
      </div>
    </template>
  </BaseCard2>

  <SelectValSessionDialog
    ref="selectValSessionDialog"
    :projectId="validationProjectId"
    :experimentId="validationExperimentId"
    :trainSessionId="validationTrainSessionId"
    :profileId="validationProfileId"
  />

  <div v-if="showDeleteConfirm" class="modal-overlay" @click="closeDeleteConfirm">
    <div class="modal-dialog" @click.stop>
      <BaseDialog
        :title="'Confirm Delete'"
        :isConfirm="true"
        :message="'Delete this profile? This action cannot be undone.'"
        :width="'420px'"
        @cancel="closeDeleteConfirm"
        @confirm="confirmDeleteProfile"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import BaseCard2 from '@/components/Cards/BaseCard2.vue';
import BaseDialog from '@/components/DilaogTemplate/BaseDialog.vue';
import IconButton from '@/components/IconButton.vue';
import SelectValSessionDialog from '@/components/ProfilerComponents/SelectValSessionDialog.vue';
import ValiListMini from '@/components/ValidationPage/ValiListMini.vue';
import { encodeID } from '@/assets/js/utils.js';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';

const props = defineProps({
  profile: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['profile-clicked', 'profile-updated']);

const $toast = useToast();
const router = useRouter();
const route = useRoute();
const dataConnect = new DatastoreConnect();
const selectValSessionDialog = ref(null);
const showDeleteConfirm = ref(false);

const profileState = computed(() => (
  props.profile?.status
  || props.profile?.status_info?.status
  || props.profile?.state
  || ''
));

const profileDescription = computed(() => (
  props.profile?.description
  || props.profile?.message
  || props.profile?.status_info?.message
  || ''
));

const profileUsername = computed(() => (
  props.profile?.username
  || '-'
));

const profileCreatedAt = computed(() => (
  props.profile?.created_date
  || ''
));

const profileCreatedAtDisplay = computed(() => {
  const rawValue = props.profile?.created_date;
  const textValue = String(rawValue || '').trim();
  if (!textValue || textValue.startsWith('0001-01-01')) {
    return '-';
  }

  const parsed = new Date(textValue);
  if (Number.isNaN(parsed.getTime())) {
    return textValue;
  }

  return parsed.toLocaleString();
});

const profileData = computed(() => {
  const profileName = (
    props.profile?.name
    || props.profile?.profile_name
    || (props.profile?.id != null ? `Profile ${props.profile.id}` : '-')
  );

  return {
    Name: profileName,
    State: profileState.value || '-',
    Date: profileCreatedAtDisplay.value,
  };
});

const validationProjectId = computed(() => pickNumericId([
  props.profile?.project_id,
  props.profile?.projectId,
  props.profile?.params?.project_id,
  props.profile?.data?.project_id,
]));

const validationExperimentId = computed(() => pickNumericId([
  props.profile?.trainer_id,
  props.profile?.trainerId,
  props.profile?.experiment_id,
  props.profile?.experimentId,
  props.profile?.params?.trainer_id,
  props.profile?.params?.experiment_id,
  props.profile?.data?.trainer_id,
  props.profile?.data?.experiment_id,
]));

const validationTrainSessionId = computed(() => pickNumericId([
  props.profile?.training_session_id,
  props.profile?.trainingSessionId,
  props.profile?.params?.training_session_id,
  props.profile?.data?.training_session_id,
]));

const validationProfileId = computed(() => pickNumericId([
  props.profile?.profile_id,
  props.profile?.id,
  props.profile?.params?.profile_id,
  props.profile?.data?.profile_id,
]));

const profileValSessions = computed(() => {
  const profile = props.profile || {};

  if (Array.isArray(profile.val_sessions)) {
    return profile.val_sessions;
  }

  if (Array.isArray(profile.vals_sessions)) {
    return profile.vals_sessions;
  }

  if (Array.isArray(profile.val_session)) {
    return profile.val_session;
  }

  if (Array.isArray(profile.params?.val_sessions)) {
    return profile.params.val_sessions;
  }

  if (Array.isArray(profile.params?.vals_sessions)) {
    return profile.params.vals_sessions;
  }

  return [];
});

function pickNumericId(candidates) {
  for (const candidate of candidates) {
    const value = Number(candidate);
    if (Number.isFinite(value) && value > 0) {
      return value;
    }
  }
  return 0;
}

function openCreateValidationSessionDialog() {
  selectValSessionDialog.value?.showModal?.();
}

function handleProfileCardClick() {
  emit('profile-clicked', props.profile);
  openProfileCharts();
}

function openProfileCharts() {
  if (!props.profile?.id) {
    return;
  }

  const isPublicRoute = route.path.includes('/public');
  const routeName = isPublicRoute ? 'public.profiler.details' : 'profiler.details';
  router.push({ name: routeName, params: { task_uid: encodeID(props.profile.id, 'bt') } });
}

async function saveDescription(nextDescription) {
  const profileId = props.profile?.id;
  if (profileId === undefined || profileId === null) {
    $toast.open({
      queue: true,
      duration: 4000,
      message: 'Missing profile id',
      type: 'error',
      position: 'top',
    });
    return;
  }

  try {
    const response = await dataConnect.api('profile.update', {
      id: profileId,
      description: nextDescription || '',
    });

    if (response?.error) {
      $toast.open({
        queue: true,
        duration: 5000,
        message: response.error.message || 'Failed to update profile description',
        type: 'error',
        position: 'top',
      });
      return;
    }

    $toast.open({
      queue: true,
      duration: 2500,
      message: 'Profile description updated',
      type: 'success',
      position: 'top',
    });

    emit('profile-updated');
  } catch (error) {
    $toast.open({
      queue: true,
      duration: 5000,
      message: String(error || 'Failed to update profile description'),
      type: 'error',
      position: 'top',
    });
  }
}

async function deleteProfile() {
  showDeleteConfirm.value = true;
}

function closeDeleteConfirm() {
  showDeleteConfirm.value = false;
}

async function confirmDeleteProfile() {
  showDeleteConfirm.value = false;
  const profileId = props.profile?.id;
  if (profileId === undefined || profileId === null) {
    $toast.open({
      queue: true,
      duration: 4000,
      message: 'Missing profile id',
      type: 'error',
      position: 'top',
    });
    return;
  }

  try {
    const response = await dataConnect.api('profile.delete', {
      id: profileId,
    });

    if (response?.error) {
      $toast.open({
        queue: true,
        duration: 5000,
        message: response.error.message || 'Failed to delete profile',
        type: 'error',
        position: 'top',
      });
      return;
    }

    $toast.open({
      queue: true,
      duration: 2500,
      message: 'Profile deleted',
      type: 'success',
      position: 'top',
    });

    emit('profile-updated');
  } catch (error) {
    $toast.open({
      queue: true,
      duration: 5000,
      message: String(error || 'Failed to delete profile'),
      type: 'error',
      position: 'top',
    });
  }
}
</script>

<style lang="scss" scoped>
.body-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.body-actions__title {
  font-size: 0.88rem;
  font-weight: 700;
  color: var(--body-text-color-secondary);
}

.add-validation-icon {
  color: var(--color-primary);
}

.validation-mini-list-wrap {
  height: 120px;
  min-height: 0;
}

:deep(.profile-card .body) {
  flex-direction: column;
  flex-wrap: nowrap;
  align-items: flex-start;
}

:deep(.profile-card .body__section) {
  flex: 0 0 auto;
  min-width: 0;
}

:deep(.profile-card .body__section--data) {
  width: fit-content;
  max-width: 100%;
  align-self: flex-start;
}

:deep(.profile-card .body__section--slot),
:deep(.profile-card .body__section--description) {
  width: 100%;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(0, 0, 0, 0.45);
  z-index: 2000;
}

.modal-dialog {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>


